"use client";

import { useActionState, useEffect, useState } from "react";

import { submitReport, type ReportState } from "./report-action";
import { useBodyScrollLock } from "@/lib/use-body-scroll-lock";

type LinkOption = { id: number; label: string };

/**
 * Ported from the `@auth` block of `profile/show.blade.php` — a signed-in
 * visitor (never the profile owner) can report the profile or one of its
 * links. Sent straight to the moderation queue, not to the owner.
 */
export function ReportDialog({
  ownerName,
  ownerId,
  links,
}: {
  ownerName: string;
  ownerId: number;
  links: LinkOption[];
}) {
  const [open, setOpen] = useState(false);
  useBodyScrollLock(open);
  const [target, setTarget] = useState("profile");
  const [canReport, setCanReport] = useState(false);
  const [state, action, pending] = useActionState<ReportState, FormData>(
    submitReport,
    null,
  );

  // Resolve the viewer client-side so the profile page stays static. Only a
  // signed-in visitor who isn't the owner sees the trigger (mirrors the
  // `@auth` + `auth()->id() !== $user->id` guard in show.blade.php).
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/session")
      .then((r) => r.json())
      .then((s: { user?: { id?: string | number } }) => {
        if (!alive) return;
        const viewerId = s?.user?.id;
        setCanReport(viewerId != null && Number(viewerId) !== ownerId);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [ownerId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const isLink = target !== "profile";
  const subjectType = isLink ? "link" : "user";
  const subjectId = isLink ? target.split(":")[1] : String(ownerId);

  if (!canReport) return null;

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-(--paw-steel-faint) hover:text-(--paw-steel)"
      >
        Report this profile
      </button>

      {open ? (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          className="fixed inset-0 z-110 flex items-center justify-center bg-(--paw-ink)/85 p-4"
        >
          <div className="paw-panel w-full max-w-md p-6 text-left">
            <h2 className="paw-heading text-lg text-white">Report</h2>
            <p className="mt-1 text-sm text-(--paw-steel-dim)">
              Sent straight to the Collr team, not to {ownerName}.
            </p>

            {state && "ok" in state ? (
              <p className="mt-4 text-sm text-(--paw-steel)">
                Report submitted. Thank you for helping keep the community safe.
              </p>
            ) : (
              <form action={action} className="mt-4 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm text-(--paw-steel-dim)">
                    What are you reporting?
                  </label>
                  <select
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    className="paw-field w-full px-3 py-2.5 text-sm"
                  >
                    <option value="profile">This profile</option>
                    {links.map((link) => (
                      <option key={link.id} value={`link:${link.id}`}>
                        {link.label}
                      </option>
                    ))}
                  </select>
                </div>

                <input type="hidden" name="subject_type" value={subjectType} />
                <input type="hidden" name="subject_id" value={subjectId} />

                <div>
                  <label className="mb-1.5 block text-sm text-(--paw-steel-dim)">
                    Reason
                  </label>
                  <textarea
                    name="reason"
                    rows={4}
                    required
                    maxLength={1000}
                    className="paw-field w-full px-3 py-2.5 text-sm"
                  />
                </div>

                {state && "error" in state ? (
                  <p className="text-sm text-(--paw-signal)">{state.error}</p>
                ) : null}

                <button
                  type="submit"
                  disabled={pending}
                  className="paw-btn w-full px-4 py-2.5 text-sm disabled:opacity-60"
                >
                  {pending ? "Submitting…" : "Submit report"}
                </button>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
